import { useCallback } from "react";
import { downloadBlobPart } from "../../../utils";
import { SnackbarMessagePropsExt, useSnackbar } from "../../ui";
import { exportToExcel, ExportToExcelProps } from "./exportToExcel";
import { MimicExcelColumn } from "./types";

export type ExportToExcelHookProps<T> = {
  getItems: (messageProps: SnackbarMessagePropsExt) => T[] | Promise<T[]>,
  columns?: MimicExcelColumn<T>[],
  getColumns?: () => MimicExcelColumn<T>[],
  fileName?: string,
  additionalProps?: ExportToExcelProps
}

export function useExportToExcel<T extends Object>(
  {
    getItems,
    columns,
    getColumns,
    fileName,
    additionalProps,
  }: ExportToExcelHookProps<T>) {
  if (!columns && !getColumns) {
    throw new Error("[useExportToExcel] Required one of properties: columns|getColumns");
  }

  const { push } = useSnackbar();

  return useCallback(async () => {
    const messageProps = push({
      title: "Exporting to xlxs",
      type: "info",
      autoClose: false,
    });
    await new Promise(resolve => setTimeout(resolve, 0));

    const items = await getItems(messageProps);

    push({
      ...messageProps,
      type: "info",
      message: "Generating file",
    });

    const xlsxFile = await exportToExcel(items, (getColumns ? getColumns() : columns) || [], additionalProps);

    push({
      ...messageProps,
      autoClose: true,
      message: "Finished",
      type: "success",
    });

    downloadBlobPart(
      xlsxFile,
      `${fileName || "Exported"}.xlsx`,
      "application/octet-stream",
    );
  }, [ push, getItems, columns, getColumns, fileName, additionalProps ]);
}
